import { useEffect, useRef, type MutableRefObject } from 'react';
import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';
import mapboxgl from 'mapbox-gl';
import { ET_COLORS, etFeatureVisible, type EtLayers, type EtResearchPayload, type EtFeature } from '../../data/et-research';

const SOURCE = 'et-research';
const PIPELINES = 'et-research-pipelines';
const LATERALS = 'et-research-laterals';
const INTERACTIVE = [PIPELINES, LATERALS];

export function etLayerFilter(layers: EtLayers): mapboxgl.FilterSpecification {
  return ['any', ['all', ['literal', layers.powergen], ['==', ['get', 'powergen'], true]], ['all', ['literal', layers.intrastate], ['==', ['get', 'intrastate'], true]]];
}

export function hasEtResearchHit(map: mapboxgl.Map, point: mapboxgl.Point): boolean {
  const layers = INTERACTIVE.filter(id => map.getLayer(id) && map.getLayoutProperty(id, 'visibility') !== 'none');
  return layers.length > 0 && map.queryRenderedFeatures(point, { layers }).length > 0;
}

export function EtFeatureDetails({ feature, data }: { feature: EtFeature; data: EtResearchPayload }) {
  const p = feature.properties;
  return <div style={{ color: 'inherit', fontSize: 12, lineHeight: 1.5 }}>
    <strong>{p.label}</strong>
    <div style={{ opacity: 0.8, margin: '3px 0' }}>{p.kind === 'lateral' ? 'Source-designated lateral' : 'ET mapped network'} · reviewed {data.reviewedAt}</div>
    {p.systemOwner && <p>Reported system owner: {p.systemOwner}</p>}
    {p.sourceRecordIds.map(id => {
      const record = data.sourceRecords[id];
      if (!record) return <p key={id}>Source record {id} is unavailable.</p>;
      const source = data.sources.find(s => s.id === record.sourceId);
      return <div key={id} style={{ borderTop: '1px solid currentColor', paddingTop: 6, marginTop: 8 }}>
        <strong>{source?.title ?? record.sourceId}</strong><br />{source?.dateLabel ?? 'Source date unverified'}
        <div style={{ opacity: 0.8, fontSize: 10 }}>Source record {id}</div>
        <p>{record.name}{record.operator && <><br />Reported operator: {record.operator}</>}{record.systemOwner && <><br />System owner: {record.systemOwner}</>}{record.product && <><br />Reported product: {record.product}</>}</p>
        {record.outsideDiameterRaw != null && <p>Raw outside diameter: {record.outsideDiameterRaw} · units unconfirmed</p>}
        {source?.note && <p>{source.note}</p>}
      </div>;
    })}
    <p>Source mapping is not a surveyed alignment. Current operating status, local diameter, pressure, available supply and tap rights remain unverified.</p>
  </div>;
}

function EtPopup({ features, data }: { features: EtFeature[]; data: EtResearchPayload }) {
  return <div style={{ maxHeight: 'min(520px, 65vh)', overflowY: 'auto', paddingRight: 4 }}>
    {features.map((feature, index) => index === 0
      ? <EtFeatureDetails key={feature.properties.id} feature={feature} data={data} />
      : <details key={feature.properties.id} style={{ marginTop: 10 }}><summary style={{ cursor: 'pointer' }}>{feature.properties.label}</summary><EtFeatureDetails feature={feature} data={data} /></details>)}
  </div>;
}

export function useEtResearchMap(mapRef: MutableRefObject<mapboxgl.Map | null>, ready: boolean, styleEpoch: number, layers: EtLayers, measuring: MutableRefObject<boolean>, data: EtResearchPayload | null) {
  const popupRef = useRef<mapboxgl.Popup | null>(null);
  const layersRef = useRef(layers);
  layersRef.current = layers;
  const active = layers.powergen || layers.intrastate;
  const isMeasuring = measuring.current;

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready || !data || !active) return;
    const byId = new Map(data.network.features.map(feature => [feature.properties.id, feature]));
    map.addSource(SOURCE, { type: 'geojson', data: {
      type: 'FeatureCollection', features: data.network.features.map(feature => ({ ...feature, properties: {
        id: feature.properties.id, kind: feature.properties.kind,
        powergen: feature.properties.powergen, intrastate: feature.properties.intrastate,
      } })),
    } });
    for (const [id, kind] of [[PIPELINES, 'pipeline'], [LATERALS, 'lateral']] as const) {
      map.addLayer({
        id, source: SOURCE, type: 'line',
        filter: ['all', etLayerFilter(layersRef.current), ['==', ['get', 'kind'], kind]],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': kind === 'lateral' ? ET_COLORS.lateral : ['case', ['==', ['get', 'powergen'], true], ET_COLORS.powergen, ET_COLORS.intrastate],
          'line-width': 3.5,
          ...(kind === 'lateral' ? { 'line-dasharray': [2, 2] } : {}),
        },
      });
    }
    let disposePopup: (() => void) | null = null;
    const close = () => {
      const dispose = disposePopup;
      disposePopup = null;
      dispose?.();
    };
    const click = (event: mapboxgl.MapMouseEvent) => {
      if (measuring.current) return;
      const available = INTERACTIVE.filter(id => map.getLayer(id));
      if (!available.length) return;
      const seen = new Set<string>();
      const features = map.queryRenderedFeatures(event.point, { layers: available }).flatMap(hit => {
        const feature = byId.get(String(hit.properties?.id));
        if (!feature || seen.has(feature.properties.id) || !etFeatureVisible(feature, layersRef.current)) return [];
        seen.add(feature.properties.id);
        return [feature];
      });
      if (!features.length) return;
      close();
      const node = document.createElement('div');
      const ownedRoot = createRoot(node);
      flushSync(() => ownedRoot.render(<EtPopup features={features} data={data} />));
      const popup = new mapboxgl.Popup({ maxWidth: '360px', closeButton: true, closeOnClick: false }).setLngLat(event.lngLat).setDOMContent(node).addTo(map);
      let released = false;
      const release = () => {
        if (released) return;
        released = true;
        if (popupRef.current === popup) popupRef.current = null;
        if (disposePopup === dispose) disposePopup = null;
        queueMicrotask(() => ownedRoot.unmount());
      };
      const dispose = () => { popup.remove(); release(); };
      disposePopup = dispose;
      popupRef.current = popup;
      popup.once('close', release);
    };
    const enter = () => { if (!measuring.current) map.getCanvas().style.cursor = 'pointer'; };
    const leave = () => { if (!measuring.current && map.getCanvas().style.cursor === 'pointer') map.getCanvas().style.cursor = ''; };
    map.on('click', click);
    for (const id of INTERACTIVE) { map.on('mouseenter', id, enter); map.on('mouseleave', id, leave); }
    return () => {
      close();
      map.off('click', click);
      for (const id of INTERACTIVE) { map.off('mouseenter', id, enter); map.off('mouseleave', id, leave); }
      leave();
      if (mapRef.current === map) {
        for (const id of INTERACTIVE) if (map.getLayer(id)) map.removeLayer(id);
        if (map.getSource(SOURCE)) map.removeSource(SOURCE);
      }
    };
  }, [mapRef, ready, styleEpoch, measuring, data, active]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    for (const [id, kind] of [[PIPELINES, 'pipeline'], [LATERALS, 'lateral']] as const) {
      if (map.getLayer(id)) map.setFilter(id, ['all', etLayerFilter(layers), ['==', ['get', 'kind'], kind]]);
    }
    popupRef.current?.remove();
    if (!measuring.current && map.getCanvas().style.cursor === 'pointer') map.getCanvas().style.cursor = '';
  }, [layers, ready, styleEpoch, mapRef, measuring]);

  useEffect(() => {
    if (isMeasuring) popupRef.current?.remove();
  }, [isMeasuring]);
}
